import { useId, useMemo, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  InputNumber,
  Radio,
  Select,
} from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import classNames from "classnames";
import dayjs from "dayjs";
import { trackNeoEvent } from "./neoAnalytics";
import styles from "./styles.module.scss";

/** Field/form shapes mirror the ``neo_form`` content block authored by
 *  the ``request_workflow_input_form`` backend tool (see
 *  ``app/helper/Neo/form_artifacts.py``). Kept permissive so an older
 *  frontend still renders a newer backend block. */
export interface NeoFormCardFieldOption {
  value: string;
  label?: string;
}

export interface NeoFormCardField {
  name: string;
  label?: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  help?: string;
  default?: unknown;
  options?: NeoFormCardFieldOption[];
}

export interface NeoFormCardData {
  type?: string;
  form_type?: string;
  title?: string;
  description?: string;
  submit_label?: string;
  cancel_label?: string;
  fields?: NeoFormCardField[];
}

type FieldValue = string | number | boolean | string[] | null | undefined;

const DATE_FORMAT = "YYYY-MM-DD";

/** Neo's LLM authors form copy, and it occasionally hands us text that was
 *  escaped for a context it never reached — most often double-escaped JSON
 *  (``\"Nope\"`` instead of ``"Nope"``) or HTML-escaped copy (``Pulse &amp;
 *  rhythm``). Rendered verbatim in a plain-text React node those show their
 *  raw markers. This decodes the common, unambiguous cases so the form reads
 *  cleanly. Frontend-only guard — applied ONLY to LLM-authored display strings
 *  (title, description, labels, placeholders, help, option labels), never to
 *  user-entered values, field ``name``s, or option ``value``s. */
function decodeFormText<T extends string | undefined>(value: T): T {
  if (typeof value !== "string" || value === "") return value;
  let out: string = value;

  // 1) Literal JS/JSON escape sequences left over from double-escaped tool
  //    JSON. ``\uXXXX`` / ``\xXX`` are the same double-escape bug as ``\"``,
  //    just for non-ASCII (e.g. ``\u2019`` → ’). ``\\`` is collapsed last so a
  //    genuinely escaped backslash survives as a single ``\``.
  if (out.indexOf("\\") !== -1) {
    out = out
      .replace(/\\u([0-9a-fA-F]{4})/g, (_m, h: string) =>
        String.fromCharCode(parseInt(h, 16))
      )
      .replace(/\\x([0-9a-fA-F]{2})/g, (_m, h: string) =>
        String.fromCharCode(parseInt(h, 16))
      )
      .replace(/\\n/g, "\n")
      .replace(/\\r/g, "\r")
      .replace(/\\t/g, "\t")
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      .replace(/\\`/g, "`")
      .replace(/\\\//g, "/")
      .replace(/\\\\/g, "\\");
  }

  // 2) HTML entities the model sometimes emits when it "escapes for HTML".
  //    ``&amp;`` is decoded last so ``&amp;lt;`` becomes ``&lt;`` (literal),
  //    not ``<``.
  if (out.indexOf("&") !== -1) {
    out = out
      .replace(/&nbsp;/gi, " ")
      .replace(/&#0*39;|&#x0*27;/gi, "'")
      .replace(/&#0*34;|&#x0*22;/gi, '"')
      .replace(/&apos;/gi, "'")
      .replace(/&quot;/gi, '"')
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&amp;/gi, "&");
  }

  return out as T;
}

const fieldLabel = (field: NeoFormCardField): string =>
  decodeFormText((field.label && field.label.trim()) || field.name);

const optionLabel = (field: NeoFormCardField, value: string): string => {
  const match = field.options?.find((o) => o.value === value);
  return decodeFormText((match?.label && match.label.trim()) || value);
};

/** Seed initial form state from each field's ``default`` (when present)
 *  and a sensible empty value for its type. */
function initialValues(fields: NeoFormCardField[]): Record<string, FieldValue> {
  const out: Record<string, FieldValue> = {};
  for (const field of fields) {
    const def = field.default;
    if (field.type === "multiselect") {
      out[field.name] = Array.isArray(def)
        ? (def as unknown[]).map((v) => String(v))
        : [];
    } else if (field.type === "checkbox") {
      out[field.name] = typeof def === "boolean" ? def : false;
    } else if (field.type === "number") {
      out[field.name] =
        typeof def === "number"
          ? def
          : typeof def === "string" && def.trim() !== ""
          ? Number(def)
          : undefined;
    } else if (def != null) {
      out[field.name] = String(def);
    } else {
      out[field.name] = field.type === "checkbox" ? false : "";
    }
  }
  return out;
}

/** Is a field's value "empty" for required-field validation? */
function isEmpty(field: NeoFormCardField, value: FieldValue): boolean {
  if (field.type === "multiselect") {
    return !Array.isArray(value) || value.length === 0;
  }
  if (field.type === "checkbox") {
    // A required checkbox means it must be checked (an explicit opt-in).
    return value !== true;
  }
  if (field.type === "number") {
    return value === undefined || value === null || Number.isNaN(value);
  }
  return value == null || String(value).trim() === "";
}

/** Render one field's value as readable text for the message Neo reads. */
function renderValue(field: NeoFormCardField, value: FieldValue): string {
  if (field.type === "checkbox") return value === true ? "Yes" : "No";
  if (field.type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    if (arr.length === 0) return "(none)";
    return arr.map((v) => optionLabel(field, v)).join(", ");
  }
  if (field.type === "select" || field.type === "radio") {
    const v = value == null ? "" : String(value);
    return v.trim() === "" ? "(blank)" : optionLabel(field, v);
  }
  const v = value == null ? "" : String(value);
  return v.trim() === "" ? "(blank)" : v.trim();
}

/** Build the human-readable answers message Neo consumes on submit. */
function serializeAnswers(
  title: string,
  fields: NeoFormCardField[],
  values: Record<string, FieldValue>
): string {
  const lines = fields.map(
    (field) =>
      `- ${fieldLabel(field)}: ${renderValue(field, values[field.name])}`
  );
  const heading = title.trim()
    ? `Here are my answers for "${title.trim()}":`
    : "Here are my answers:";
  return `${heading}\n${lines.join("\n")}`;
}

export interface NeoInputFormCardProps {
  form: NeoFormCardData;
  /** When true the form is non-interactive (e.g. the workflow already
   *  advanced and re-locked the conversation). */
  disabled?: boolean;
  /** Send the serialized answers back as a turn so Neo can use them.
   *  A returned promise is awaited: if it rejects, the card un-settles itself
   *  with every answer still filled in, so the user can retry rather than
   *  being shown "Submitted" over input that never reached the server. */
  onSubmit: (serializedText: string) => void | Promise<void>;
  /** Optional cancel handler; receives a short cancel message. */
  onCancel?: (message: string) => void;
  /** When false, the Cancel button is hidden so the only way forward is to
   *  submit. Used for interactive workflow ``user_input`` steps, where the
   *  free-text composer is hidden and cancelling would strand the user with no
   *  way to respond. Defaults to true. */
  allowCancel?: boolean;
}

const NeoInputFormCard = ({
  form,
  disabled = false,
  onSubmit,
  onCancel,
  allowCancel = true,
}: NeoInputFormCardProps) => {
  const fields = useMemo<NeoFormCardField[]>(
    () => (Array.isArray(form.fields) ? form.fields : []),
    [form.fields]
  );
  const title = decodeFormText(
    (form.title && form.title.trim()) || "Provide input"
  );
  const submitLabel =
    decodeFormText(form.submit_label && form.submit_label.trim()) || "Submit";
  const cancelLabel =
    decodeFormText(form.cancel_label && form.cancel_label.trim()) || "Cancel";
  const description = decodeFormText(form.description);

  const [values, setValues] = useState<Record<string, FieldValue>>(() =>
    initialValues(fields)
  );
  const [errors, setErrors] = useState<Set<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // What the live region says after the most recent submit attempt. Inline
  // per-field errors alone are silent to a screen reader and invisible to
  // anyone whose first invalid field scrolled out of view.
  const [announcement, setAnnouncement] = useState("");

  const fieldsRef = useRef<HTMLDivElement | null>(null);
  const idPrefix = useId();
  const domId = (name: string, suffix = "") =>
    `${idPrefix}${name}${suffix ? `-${suffix}` : ""}`;

  const settled = submitted || cancelled;
  const locked = disabled || settled || submitting;

  const setValue = (name: string, value: FieldValue) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors.has(name)) {
      setErrors((prev) => {
        const next = new Set(prev);
        next.delete(name);
        return next;
      });
    }
  };

  /** Move focus to a field's control. Every Ant Design control we render wraps
   *  a real focusable input, so reaching for it by DOM query works uniformly
   *  across text, select, date, checkbox, and radio without a ref per type. */
  const focusField = (name: string) => {
    const wrapper = fieldsRef.current?.querySelector<HTMLElement>(
      `[data-field-name="${CSS.escape(name)}"]`
    );
    const control = wrapper?.querySelector<HTMLElement>(
      "input:not([disabled]), textarea:not([disabled])"
    );
    (control ?? wrapper)?.focus();
  };

  const handleSubmit = async () => {
    if (locked) return;
    const missing = new Set<string>();
    for (const field of fields) {
      if (field.required && isEmpty(field, values[field.name])) {
        missing.add(field.name);
      }
    }
    if (missing.size > 0) {
      setErrors(missing);
      setSubmitError(null);
      const first = fields.find((f) => missing.has(f.name));
      setAnnouncement(
        missing.size === 1
          ? `${fieldLabel(first ?? fields[0])} is required.`
          : `${missing.size} required fields are missing. Starting with ${
              first ? fieldLabel(first) : "the first"
            }.`
      );
      if (first) focusField(first.name);
      return;
    }
    const text = serializeAnswers(title, fields, values);
    setSubmitError(null);
    setAnnouncement("");
    setSubmitting(true);
    trackNeoEvent("workflow input form submitted", {
      field_count: fields.length,
    });
    try {
      await onSubmit(text);
      setSubmitted(true);
    } catch (err) {
      console.error("[NeoInputFormCard] submit failed", err);
      // Nothing was accepted, so leave the form exactly as the user left it —
      // answers intact, buttons live — instead of claiming "Submitted".
      setSubmitError("Couldn’t send your answers. Please try again.");
      setAnnouncement("Couldn’t send your answers. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (locked) return;
    setCancelled(true);
    trackNeoEvent("workflow input form cancelled", {
      field_count: fields.length,
    });
    onCancel?.(`I don't want to fill out the "${title}" form right now.`);
  };

  const renderControl = (field: NeoFormCardField) => {
    const value = values[field.name];
    const placeholder = decodeFormText(field.placeholder);
    const invalid = errors.has(field.name);
    // Point the control at whichever of its help/error texts are rendered, so
    // a screen reader reads them as part of the field rather than as stray
    // text somewhere after it.
    const describedBy =
      [
        field.help ? domId(field.name, "help") : null,
        invalid ? domId(field.name, "error") : null,
      ]
        .filter(Boolean)
        .join(" ") || undefined;
    const common = {
      id: domId(field.name),
      disabled: locked,
      status: invalid ? ("error" as const) : undefined,
      "aria-invalid": invalid || undefined,
      "aria-describedby": describedBy,
    };
    switch (field.type) {
      case "textarea":
        return (
          <Input.TextArea
            {...common}
            value={typeof value === "string" ? value : ""}
            placeholder={placeholder}
            rows={3}
            onChange={(e) => setValue(field.name, e.target.value)}
          />
        );
      case "number":
        return (
          <InputNumber
            {...common}
            className={styles.neoFormControlFull}
            value={typeof value === "number" ? value : undefined}
            placeholder={placeholder}
            onChange={(v) => setValue(field.name, v as FieldValue)}
          />
        );
      case "select":
        return (
          <Select
            {...common}
            className={styles.neoFormControlFull}
            value={value === "" || value == null ? undefined : String(value)}
            placeholder={placeholder || "Select…"}
            options={(field.options ?? []).map((o) => ({
              value: o.value,
              label: decodeFormText(o.label ?? o.value),
            }))}
            onChange={(v) => setValue(field.name, v as FieldValue)}
            allowClear
          />
        );
      case "multiselect":
        return (
          <Select
            {...common}
            className={styles.neoFormControlFull}
            mode="multiple"
            value={Array.isArray(value) ? value : []}
            placeholder={placeholder || "Select…"}
            options={(field.options ?? []).map((o) => ({
              value: o.value,
              label: decodeFormText(o.label ?? o.value),
            }))}
            onChange={(v) => setValue(field.name, v as FieldValue)}
          />
        );
      case "date":
        return (
          <DatePicker
            {...common}
            className={styles.neoFormControlFull}
            value={
              typeof value === "string" && value
                ? dayjs(value, DATE_FORMAT)
                : null
            }
            format={DATE_FORMAT}
            onChange={(d) =>
              setValue(field.name, d ? d.format(DATE_FORMAT) : "")
            }
          />
        );
      case "checkbox":
        return (
          <Checkbox
            id={common.id}
            disabled={locked}
            aria-invalid={common["aria-invalid"]}
            aria-describedby={describedBy}
            checked={value === true}
            onChange={(e) => setValue(field.name, e.target.checked)}
          >
            {placeholder || fieldLabel(field)}
          </Checkbox>
        );
      case "radio":
        return (
          <Radio.Group
            disabled={locked}
            // A radio group's accessible name comes from the group, not from a
            // label pointing at any one radio, so it is labelled directly.
            aria-label={fieldLabel(field)}
            aria-invalid={common["aria-invalid"]}
            aria-describedby={describedBy}
            value={value == null ? undefined : String(value)}
            onChange={(e) => setValue(field.name, e.target.value)}
          >
            {(field.options ?? []).map((o) => (
              <Radio key={o.value} value={o.value}>
                {decodeFormText(o.label ?? o.value)}
              </Radio>
            ))}
          </Radio.Group>
        );
      case "text":
      default:
        return (
          <Input
            {...common}
            value={typeof value === "string" ? value : ""}
            placeholder={placeholder}
            onChange={(e) => setValue(field.name, e.target.value)}
          />
        );
    }
  };

  return (
    <div className={classNames(styles.neoFormInline)}>
      <div className={styles.neoFormHeader}>
        <span className={styles.neoFormTitle}>{title}</span>
        {submitted ? (
          <span
            className={classNames(
              styles.neoFormStatus,
              styles.neoFormStatusDone
            )}
          >
            Submitted
          </span>
        ) : cancelled ? (
          <span className={styles.neoFormStatus}>Cancelled</span>
        ) : null}
      </div>
      {description ? (
        <p className={styles.neoFormDescription}>{description}</p>
      ) : null}

      <div className={styles.neoFormFields} ref={fieldsRef}>
        {fields.map((field) => (
          <div
            key={field.name}
            className={styles.neoFormField}
            data-field-name={field.name}
          >
            {field.type !== "checkbox" && field.type !== "radio" ? (
              <label
                className={styles.neoFormLabel}
                htmlFor={domId(field.name)}
              >
                {fieldLabel(field)}
                {field.required ? (
                  <span className={styles.neoFormRequired}> *</span>
                ) : null}
              </label>
            ) : null}
            {renderControl(field)}
            {field.help ? (
              <span
                className={styles.neoFormHelp}
                id={domId(field.name, "help")}
              >
                {decodeFormText(field.help)}
              </span>
            ) : null}
            {errors.has(field.name) ? (
              <span
                className={styles.neoFormError}
                id={domId(field.name, "error")}
              >
                This field is required.
              </span>
            ) : null}
          </div>
        ))}
      </div>

      {/* Announces validation and submit outcomes. Assertive because it always
          follows a deliberate submit press, and the user is waiting on it. */}
      <div className={styles.neoFormSrOnly} role="alert" aria-live="assertive">
        {announcement}
      </div>

      {submitError ? (
        <p className={styles.neoFormSubmitError}>{submitError}</p>
      ) : null}

      {!settled ? (
        <div className={styles.neoFormActions}>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            disabled={disabled || settled}
            loading={submitting}
            onClick={() => void handleSubmit()}
          >
            {submitError ? "Try again" : submitLabel}
          </Button>
          {allowCancel && onCancel ? (
            <Button
              size="small"
              icon={<CloseOutlined />}
              disabled={locked}
              onClick={handleCancel}
            >
              {cancelLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default NeoInputFormCard;
